import { useMemo } from 'react';

import { Box, Typography } from '@mui/material';

import ChartShell from '@/components/Chart/ChartShell';
import useSeriesVisibility from '@/components/Chart/hooks/useSeriesVisibility';
import type {
  ChartType,
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import { applySeriesVisibility } from '@/components/Chart/utils/seriesVisibility';
import { DEFAULT_CHART_HEIGHT, TABLE_ROW_HEIGHT_PX } from '@/constants/constants';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import Loader from '@/shared/ui/feedback/Spinner';

import LegendDropdown from './LegendDropdown';
import useRegularChart from './useRegularChart';

const RegularChart = ({
  chartSeries,
  height,
  error,
  loading,
  showLegend = true,
  options,
  chartType = 'spot',
  chartId
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  height?: number;
  error: boolean;
  loading: boolean;
  showLegend?: boolean;
  options: ExtendedOptions | undefined;
  chartType?: ChartType;
  chartId?: string;
}) => {
  const { chartContainerRef } = useRegularChart({
    chartSeries
  });
  const isStockChart = chartType === 'spot';

  const chartContainerHeight = useMemo(
    () => (height ? height * TABLE_ROW_HEIGHT_PX : DEFAULT_CHART_HEIGHT),
    [height]
  );
  const seriesSource = options?.series ?? chartSeries;
  const { hiddenSeriesIds, setHiddenSeriesIds } = useSeriesVisibility(seriesSource);
  const chartOptions = useMemo(() => {
    if (!options) return options;
    const seriesWithVisibility = applySeriesVisibility(
      options.series ?? chartSeries,
      hiddenSeriesIds
    );
    return {
      ...options,
      series: seriesWithVisibility
    };
  }, [chartSeries, hiddenSeriesIds, options]);

  const legendSeries = useMemo(() => {
    if (!seriesSource?.length) return [];
    return seriesSource.filter(series => !series.name?.includes('Navigator'));
  }, [seriesSource]);
  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          height: chartContainerHeight,
          overflowX: 'hidden'
        }}
      >
        <Typography color='info'>
          We couldn't retrieve your data. Please try again later.
        </Typography>
      </Box>
    );
  }
  if (!loading && !chartSeries?.length) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          height: chartContainerHeight,
          overflowX: 'hidden'
        }}
      >
        <EmptyState />
      </Box>
    );
  }

  if (!loading && !chartSeries?.length) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          height: chartContainerHeight,
          overflowX: 'hidden'
        }}
      >
        <EmptyState />
      </Box>
    );
  }
  if (loading || !chartSeries?.length) {
    return (
      <Box sx={{ height: chartContainerHeight }}>
        <Loader />
      </Box>
    );
  }
  return (
    <Box
      ref={chartContainerRef}
      sx={{
        width: '100%',
        overflowX: 'hidden'
      }}
    >
      <Box className={chartType === 'spot' ? 'regular-chart' : 'base-chart'}>
        <ChartShell
          chartId={chartId}
          containerProps={{ id: chartId }}
          isStockChart={isStockChart}
          options={chartOptions}
        />
        {showLegend && legendSeries.length > 0 && (
          <LegendDropdown
            hiddenSeriesIds={hiddenSeriesIds}
            series={legendSeries}
            setHiddenSeriesIds={setHiddenSeriesIds}
          />
        )}
      </Box>
    </Box>
  );
};
export default RegularChart;
